import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Plus, Pencil, Trash2, UserCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetStorageAccountsQuery,
  useGetStorageSourceQuery,
  useGetStorageProvidersQuery,
  useCreateStorageAccountMutation,
  useUpdateStorageAccountMutation,
  useDeleteStorageAccountMutation,
} from '@/store/api/apiSlice';
import type { StorageAccount, StorageProviderDescriptor, StorageProviderFormField } from '@/types';
import { formatBytes } from '@/lib/storage-transfer';
import { useToast } from '@/components/shared/Toast';
import { wsClient } from '@/lib/ws';
import { StorageEventType, STORAGE_UPDATES_ROOM } from '@cluster/types';
import { computeUsagePercent } from './StorageSourcesPage';

export function StorageAccountsPage() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<StorageAccount | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StorageAccount | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    providerConfig: {} as Record<string, unknown>,
    capacityBytes: 0,
  });

  const { data: source } = useGetStorageSourceQuery(sourceId!);
  const { data: accounts, isLoading, refetch: refetchAccounts } = useGetStorageAccountsQuery(sourceId!);
  const { data: providers } = useGetStorageProvidersQuery();
  const [createAccount, { isLoading: isCreating }] = useCreateStorageAccountMutation();
  const [updateAccount, { isLoading: isUpdating }] = useUpdateStorageAccountMutation();
  const [deleteAccount, { isLoading: isDeleting }] = useDeleteStorageAccountMutation();

  // Use ref to access refetch without causing the WS-effect to re-run on arg changes
  const refetchAccountsRef = useRef(refetchAccounts);
  useEffect(() => {
    refetchAccountsRef.current = refetchAccounts;
  }, [refetchAccounts]);

  // WebSocket real-time refresh (no polling)
  useEffect(() => {
    const onAccountEvent = () => { refetchAccountsRef.current(); };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(StorageEventType.UPLOAD_COMPLETED, onAccountEvent),
      wsClient.on(StorageEventType.UPLOAD_FAILED, onAccountEvent),
      wsClient.on(StorageEventType.FILE_DELETED, onAccountEvent),
      wsClient.on(StorageEventType.REPLICATION_STATUS, onAccountEvent),
    ];

    return () => {
      unsubs.forEach((u) => u());
      wsClient.unsubscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    };
  }, []);

  const accountList = (accounts as StorageAccount[] | undefined) ?? [];
  const providerList = (providers as StorageProviderDescriptor[] | undefined) ?? [];
  const providerDescriptor = providerList.find((p) => p.type === source?.type);
  const accountFormFields = providerDescriptor?.accountFormFields ?? [];

  const columns: ColumnDef<StorageAccount, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'capacityBytes',
      header: 'Capacity',
      cell: ({ row }) => formatBytes(row.original.capacityBytes),
    },
    {
      accessorKey: 'usedBytes',
      header: 'Usage',
      cell: ({ row }) => {
        const used = row.original.usedBytes ?? 0;
        const cap = row.original.capacityBytes ?? 0;
        const pct = computeUsagePercent(used, cap);
        return (
          <div className="flex items-center gap-2 min-w-[120px]">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatBytes(used)} ({pct}%)
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={row.original.isActive ? 'active' : 'stopped'} />
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row.original)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(row.original)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditingAccount(null);
    setFormData({ name: '', providerConfig: {}, capacityBytes: 0 });
    setModalOpen(true);
  }

  function openEdit(account: StorageAccount) {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      providerConfig: { ...account.providerConfig },
      capacityBytes: account.capacityBytes,
    });
    setModalOpen(true);
  }

  function handleConfigFieldChange(key: string, value: unknown) {
    setFormData((prev) => ({
      ...prev,
      providerConfig: { ...prev.providerConfig, [key]: value },
    }));
  }

  /** Serialize a record-typed field value to editable text. */
  function recordFieldToText(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
    return '';
  }

  /** Parse textarea text back into a record value (empty/invalid -> '' which gets pruned on save). */
  function parseRecordFieldText(text: string): unknown {
    const trimmed = text.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      return '';
    } catch {
      return '';
    }
  }

  /** Render the dynamic providerConfig credential fields for the selected source's provider. */
  function renderAccountConfigFields() {
    if (!accountFormFields.length) {
      return (
        <div className="text-xs text-muted-foreground">
          No provider credential fields configured for {source?.type ?? 'this provider'}.
        </div>
      );
    }
    return (
      <div className="space-y-3 rounded-lg border p-3">
        <p className="text-sm font-medium">Provider Credentials</p>
        {accountFormFields.map((field) => (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={`acct-${field.name}`}>
              {field.label}
              {field.required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
            {field.type === 'select' ? (
              <select
                id={`acct-${field.name}`}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={String(field.name in formData.providerConfig
                  ? formData.providerConfig[field.name]
                  : field.defaultValue ?? '')}
                onChange={(e) => handleConfigFieldChange(field.name, e.target.value)}
              >
                <option value="">Select...</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : field.type === 'number' ? (
              <Input
                id={`acct-${field.name}`}
                type="number"
                value={String(field.name in formData.providerConfig
                  ? formData.providerConfig[field.name]
                  : field.defaultValue ?? '')}
                onChange={(e) => handleConfigFieldChange(field.name, Number(e.target.value))}
              />
            ) : field.type === 'boolean' ? (
              <input
                type="checkbox"
                id={`acct-${field.name}`}
                checked={Boolean(formData.providerConfig[field.name] ?? field.defaultValue)}
                onChange={(e) => handleConfigFieldChange(field.name, e.target.checked)}
              />
            ) : field.type === 'record' ? (
              <textarea
                id={`acct-${field.name}`}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                value={recordFieldToText(
                  field.name in formData.providerConfig
                    ? formData.providerConfig[field.name]
                    : field.defaultValue,
                )}
                onChange={(e) => handleConfigFieldChange(field.name, parseRecordFieldText(e.target.value))}
                placeholder='{"key": "value"}'
              />
            ) : (
              <Input
                id={`acct-${field.name}`}
                type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                value={String(field.name in formData.providerConfig
                  ? formData.providerConfig[field.name]
                  : field.defaultValue ?? '')}
                onChange={(e) => handleConfigFieldChange(field.name, e.target.value)}
              />
            )}
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  async function handleSave() {
    if (!sourceId) return;

    // Client-side required-field validation (name + required provider credential fields)
    const missing: string[] = [];
    if (!formData.name.trim()) missing.push('name');
    accountFormFields
      .filter((f) => f.required)
      .forEach((f) => {
        const value = formData.providerConfig[f.name];
        if (value === undefined || value === null || value === '') missing.push(f.name);
      });
    if (missing.length > 0) {
      toastError(`Faltan campos obligatorios: ${missing.join(', ')}`);
      return;
    }

    const payload: Partial<StorageAccount> = {
      name: formData.name.trim(),
      providerConfig: formData.providerConfig,
      capacityBytes: formData.capacityBytes,
    };

    try {
      if (editingAccount) {
        await updateAccount({
          accountId: editingAccount._id,
          body: payload,
        }).unwrap();
      } else {
        await createAccount({ sourceId, body: payload }).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      const data = (err as {
        data?: {
          error?: { message?: string; details?: Array<{ field?: string; message?: string }> };
          message?: string;
          details?: Array<{ field?: string; message?: string }>;
        };
      })?.data;
      const details = data?.error?.details ?? data?.details;
      let msg =
        data?.error?.message ??
        data?.message ??
        'No se pudo guardar la cuenta de almacenamiento. Inténtalo de nuevo.';
      if (Array.isArray(details) && details.length > 0) {
        msg = details
          .map((d) => (d.field ? `${d.field}: ${d.message ?? ''}` : d.message))
          .filter(Boolean)
          .join(', ');
      }
      toastError(msg);
    }
  }

  async function handleDelete() {
    if (deleteTarget && sourceId) {
      await deleteAccount({ accountId: deleteTarget._id }).unwrap();
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/storage/sources')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            Accounts — {source?.name ?? 'Loading...'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage storage accounts for this source
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Account
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : accountList.length === 0 ? (
            <EmptyState
              title="No accounts"
              description="Add an account to this storage source"
              action={<Button onClick={openCreate}>Add Account</Button>}
            />
          ) : (
            <DataTable columns={columns} data={accountList} />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
            <DialogDescription>
              {editingAccount
                ? 'Update account configuration.'
                : 'Configure a new storage account.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="account-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacity">Capacity (bytes)</Label>
              <Input
                id="capacity"
                type="number"
                value={formData.capacityBytes}
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => setFormData({ ...formData, capacityBytes: Number(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">
                {formData.capacityBytes > 0 ? formatBytes(formData.capacityBytes) : 'No capacity set'}
              </p>
            </div>

            {/* Dynamic provider credential fields */}
            {renderAccountConfigFields()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Account"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
